import React from "react";
import {
  CalendarIcon,
  CancelCircleIcon,
  CheckCircleIcon,
  HourglassIcon,
  LoadPhase,
  SkeletonCard,
} from "../../../components/ui";
import type { TaskSummary } from "../myTask";

interface TileProps {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  /** The disc behind the glyph, and the glyph's own ink. */
  tint: string;
  ink: string;
  label: string;
  count: number;
  phase: LoadPhase;
  /** The day never arrived, so there is no figure — a dash, not a zero. */
  failed: boolean;
}

const Tile: React.FC<TileProps> = ({
  Icon,
  tint,
  ink,
  label,
  count,
  phase,
  failed,
}) => (
  // Tighter all round below `sm`, where two of these share a 360px row: at
  // the desktop's padding and disc the label had about 74px left to print
  // "Appointments" in and came out as "Appointment…". The figure keeps its
  // size — it is the thing being read.
  <div className="flex items-center gap-2.5 sm:gap-3 card-surface border border-line bg-white px-3 py-3 sm:px-4 sm:py-3.5 3xl:px-5 3xl:py-4">
    <span
      aria-hidden="true"
      className={`flex h-9 w-9 sm:h-[38px] sm:w-[38px] 3xl:h-11 3xl:w-11 flex-shrink-0 items-center justify-center rounded-[6px] ${tint} ${ink}`}
    >
      <Icon className="h-[18px] w-[18px] sm:h-[20px] sm:w-[20px] 3xl:h-6 3xl:w-6" />
    </span>
    <div className="min-w-0">
      {/* A count is a count of rows that are not in yet, so while the day is
          out there is no figure to write — and a "0" here would read as a free
          day. A day that never arrived is the same: a dash, not a zero. The
          line is held at its own height either way, so nothing shifts when the
          number lands. */}
      {failed ? (
        <div className="flex h-[27px] 3xl:h-[32px] items-center text-[20px] 3xl:text-[24px] font-medium leading-tight text-ink-muted">
          -
        </div>
      ) : phase === "ready" ? (
        <div
          className="text-[20px] 3xl:text-[24px] font-medium leading-tight text-ink"
          aria-label={`${count} ${label.toLowerCase()}`}
        >
          {count}
        </div>
      ) : (
        <div className="flex h-[27px] 3xl:h-[32px] items-center">
          <SkeletonCard phase={phase} className="h-5 w-[42px]" />
        </div>
      )}
      <p className="truncate text-[12px] sm:text-[13px] 3xl:text-[14px] text-ink-muted">
        {label}
      </p>
    </div>
  </div>
);

export interface TaskSummaryCardsProps {
  summary: TaskSummary;
  /** Where the day's read is. All four wait, shimmer and land together. */
  phase?: LoadPhase;
  /** The day could not be loaded, so all four read as a dash rather than 0. */
  failed?: boolean;
}

/**
 * The day in four figures: how much is booked, how much has been seen
 * through, how much is still to come, and how much was called off.
 *
 * They deliberately do not add up — see `taskSummary`, which is where the
 * reason lives. An appointment whose time is up and that nobody has marked is
 * counted in the first tile and in none of the other three, because this app
 * will not read "completed" off a clock.
 *
 * Four cards with air between them rather than the revenue screen's single
 * divided strip. That strip is three parts of one sum; these are four separate
 * counts of the same day, and the design draws them apart.
 *
 * Two across on a phone rather than one, which is what the design draws and
 * what the figures want: these are four counts of one day and they are read
 * against each other — five booked, two of them done — so stacking them in a
 * column puts three of the four below the fold and turns a glance into a
 * scroll. Four across needs more width than a phone has; two by two is the
 * square that fits.
 */
const TaskSummaryCards: React.FC<TaskSummaryCardsProps> = ({
  summary,
  phase = "ready",
  failed = false,
}) => (
  <section
    aria-label="This day at a glance"
    className="grid grid-cols-2 gap-3 xl:grid-cols-4 3xl:gap-4"
  >
    <Tile
      Icon={CalendarIcon}
      tint="bg-[#F3EEFF]"
      ink="text-[#7C5CFC]"
      label="Appointments"
      count={summary.total}
      phase={phase}
      failed={failed}
    />
    <Tile
      Icon={CheckCircleIcon}
      tint="bg-[#E7F8EF]"
      ink="text-[#2F8E5B]"
      label="Completed"
      count={summary.completed}
      phase={phase}
      failed={failed}
    />
    <Tile
      Icon={HourglassIcon}
      tint="bg-[#E8F1FD]"
      ink="text-[#3B72D4]"
      label="Upcoming"
      count={summary.upcoming}
      phase={phase}
      failed={failed}
    />
    <Tile
      Icon={CancelCircleIcon}
      tint="bg-[#F1F3F5]"
      ink="text-[#62636C]"
      label="Cancelled"
      count={summary.cancelled}
      phase={phase}
      failed={failed}
    />
  </section>
);

export default TaskSummaryCards;
